import $ from 'jquery';
import { HEADLINES, HTML_BLOCK_RESULT_KEYS, resultLabel } from './labels';

/*
 * Issue #227, AC-3: one generic capture routine for all eight calculators.
 *
 * It reads the existing DOM contract every calculator already shares -- [data-input] with a name
 * attribute, [data-result] with a key -- rather than knowing anything per calculator. A ninth
 * calculator built on the same templates is captured with no change here, which is what AC-3's
 * "future calculators inherit it" requires.
 *
 * Three real quirks it has to handle, all confirmed in the templates:
 *
 *   1. Buydown's panes contain five mutually exclusive [data-toggle-block] groups with DUPLICATE input
 *      names. Reading the whole pane would capture all five structures' inputs and the last one would
 *      win. calculator-common.js solves this by reading only the visible block; so does this.
 *   2. Refinance writes results into disabled <input> elements, so some [data-result] values live in
 *      .val() rather than .text().
 *   3. Refinance and 15-vs-30 inject a whole HTML block into data-result="basicTitle"/"advancedTitle"
 *      instead of labelled leaf values. Those are read as headline text, not as table rows.
 */

const AMORTIZATION_PANE_ID = 'amortization';
// Mirrors SaveResultsValidator.MaxAmortizationYears: a 50-year schedule started mid-year spans 51
// calendar years. Truncating here beats letting an oversized schedule fail the whole submission's
// server-side validation.
const MAX_AMORTIZATION_YEARS = 51;

function visibleScope(pane) {
    // Mirrors calculator-common.js's own rule for the toggle-block calculators.
    const visibleBlock = pane.find('[data-toggle-block]').not('.d-none');
    return visibleBlock.length ? visibleBlock.first() : pane;
}

function inputLabel(el) {
    const $el = $(el);

    // The visible label is the first <p> in the enclosing .panel__item. There are no <label for>
    // elements in these templates.
    const fromDom = $el.closest('.panel__item').find('p').first().text().trim();
    if (fromDom) return fromDom;

    // Several inputs carry a title attribute matching their name, which is better than nothing.
    return ($el.attr('title') || $el.attr('name') || '').trim();
}

function readValue($el) {
    return $el.is('input, select, textarea')
        ? ($el.val() || '').toString().trim()
        : $el.text().trim();
}

function inputAffixes($el) {
    // The templates mark a currency input with an <i>$</i> icon on the left and a percent input with
    // <i>%</i> on the right (.panel__input.icon--left / .icon--right). That icon is the only
    // machine-readable unit information the DOM carries, so the modal's summary line reads it rather
    // than knowing units per calculator.
    const $wrap = $el.closest('.panel__input');
    const icon = $wrap.find('i').first().text().trim();

    return {
        prefix: $wrap.hasClass('icon--left') ? icon : '',
        suffix: $wrap.hasClass('icon--right') ? icon : ''
    };
}

function captureInputs(scope) {
    const inputs = [];

    scope.find('[data-input]').each((i, el) => {
        const $el = $(el);
        const name = $el.attr('name');
        if (!name) return;

        const value = readValue($el);
        if (!value) return;

        inputs.push(Object.assign({ name, label: inputLabel(el), value }, inputAffixes($el)));
    });

    return inputs;
}

function captureResults(scope) {
    const results = [];
    const htmlBlocks = [];

    scope.find('[data-result]').each((i, el) => {
        const $el = $(el);
        const key = $el.attr('data-result');
        if (!key) return;

        const value = readValue($el);
        if (!value) return;

        if (HTML_BLOCK_RESULT_KEYS.indexOf(key) !== -1) {
            // Collapse the injected block's whitespace so it reads as one sentence.
            htmlBlocks.push(value.replace(/\s+/g, ' ').trim());
            return;
        }

        const domFallback = $el.closest('.panel__item, .result-lines, .panel--right, .panel--bottom')
            .find('p').first().text().trim();

        results.push({ name: key, label: resultLabel(key, domFallback), value });
    });

    return { results, htmlBlocks };
}

function visibleCells(row, selector) {
    // The schedule's "Year No." column carries an inline display:none on both its <th> and <td>
    // (table-template.hbs) -- the visitor never sees it, so the email must not either. The check
    // reads the inline style directly: jQuery's :visible is wrong here, because inside a collapsed
    // year panel EVERY cell reports as hidden.
    const cells = [];
    row.find(selector).each((i, el) => {
        if (el.style && el.style.display === 'none') return;
        cells.push($(el).text().trim());
    });
    return cells;
}

function captureAmortization(pane) {
    // The on-page schedule (#amortizationTable) is an accordion: one .table__item per calendar
    // year, each with its own <table> of that year's monthly rows. Collapsed and `d-none` years are
    // still in the DOM, so this captures the FULL schedule -- the email renders the same year/month
    // breakdown the calculator shows with "see more years" expanded.
    const items = pane.find('.table__item');
    if (!items.length) return null;

    const columns = visibleCells(items.first().find('thead tr').first(), 'th');
    if (!columns.length) return null;

    const years = [];
    items.each((i, item) => {
        if (years.length >= MAX_AMORTIZATION_YEARS) return false;

        const $item = $(item);
        const year = $item.find('.table__button').first().text().trim();

        const rows = [];
        $item.find('tbody tr').each((j, tr) => {
            const cells = visibleCells($(tr), 'td');
            if (cells.length) rows.push(cells);
        });

        if (year && rows.length) years.push({ year, rows });
    });

    if (!years.length) return null;

    return { columns, years };
}

/*
 * The mockup's refinance email carries a sub-line under the headline figure ("Plus $3,500 cash
 * available at closing"). Generic: any HEADLINES spec can name a subKey whose captured figure is
 * spliced into subTemplate at {value}. Skipped when the figure is missing or non-positive --
 * "Plus $0.00 cash available at closing" would be worse than no sub-line.
 */
function buildSubLabel(spec, find, prefixed) {
    if (!spec.subKey || !spec.subTemplate) return null;

    const raw = find(spec.subKey);
    if (!raw) return null;

    const numeric = parseFloat(raw.replace(/[^0-9.-]/g, ''));
    if (!isFinite(numeric) || numeric <= 0) return null;

    return spec.subTemplate.replace('{value}', prefixed(raw));
}

function buildHeadline(calculatorSlug, results, htmlBlocks) {
    const spec = HEADLINES[calculatorSlug];
    if (!spec) {
        return results.length
            ? { label: results[0].label, value: results[0].value }
            : { label: null, value: null };
    }

    if (spec.htmlBlock) {
        return { label: spec.label, value: htmlBlocks.length ? htmlBlocks[0] : null };
    }

    const find = (key) => {
        const match = results.filter((r) => r.name === key)[0];
        return match ? match.value : null;
    };

    const primary = find(spec.key);
    if (!primary) return { label: spec.label, value: null };

    // Both guarded, because the templates are inconsistent: most render the $ in an <i> OUTSIDE the
    // captured span (mortgage, purchasing power, rent vs buy, 15-vs-30, bi-weekly), but buydown and
    // qualifying carry it inside the value already. Sign-aware, because refinance savings can be
    // NEGATIVE (new payment higher than current): currency.js formats those as "-$123.45", whose
    // prefix sits at index 1, so the naive index-0 guard failed open and produced "$-$123.45" in
    // the modal, the stored record and the email headline (PR #269 review). The prefix goes after
    // the sign, and a value already carrying it anywhere is left alone.
    const prefixed = (v) => {
        if (!spec.prefix || v.indexOf(spec.prefix) !== -1) return v;
        if (v.charAt(0) === '-') return '-' + spec.prefix + v.slice(1);
        return spec.prefix + v;
    };
    const suffixed = (v) => (spec.suffix && v.slice(-spec.suffix.length) !== spec.suffix
        ? v + spec.suffix
        : v);

    const subLabel = buildSubLabel(spec, find, prefixed);

    if (spec.secondKey) {
        const secondary = find(spec.secondKey);
        if (secondary) {
            return {
                label: spec.label,
                value: suffixed(prefixed(primary) + spec.join + prefixed(secondary)),
                subLabel
            };
        }
    }

    return { label: spec.label, value: suffixed(prefixed(primary)), subLabel };
}

/**
 * Captures the active pane's scenario.
 *
 * @param {jQuery} calculatorRoot the [data-calc-mode] element
 * @param {string} calculatorSlug the calculator's route slug
 * @returns {object|null} the capture, or null when there is nothing to save
 */
export function captureScenario(calculatorRoot, calculatorSlug) {
    const activePane = calculatorRoot.find('.tab-pane.active').first();
    if (!activePane.length) return null;

    const activeTab = (activePane.attr('id') || '').toLowerCase();

    // The Amortization pane holds only the schedule -- no inputs and no [data-result] elements. The
    // scenario that produced that schedule lives in the Basic pane, so it is captured from there and
    // the schedule travels alongside it. Without this, an amortization-tab submission would save a
    // record with no inputs and no results and email the visitor an empty table.
    const sourcePane = activeTab === AMORTIZATION_PANE_ID
        ? (calculatorRoot.find('.tab-pane#basic').first().length
            ? calculatorRoot.find('.tab-pane#basic').first()
            : activePane)
        : activePane;

    // Inputs come from the visible toggle block only: buydown's five blocks carry DUPLICATE input
    // names and the hidden ones would win. Results deliberately do NOT share that scope -- buydown
    // (the only toggle-block calculator) renders its [data-result] elements after the blocks, outside
    // all five, so scoping results to the visible block found nothing and the Save Results click died
    // on the nothing-calculated guard below. No [data-result] sits inside any toggle block, so the
    // duplicate-name hazard does not exist for results.
    const inputs = captureInputs(visibleScope(sourcePane));
    const { results, htmlBlocks } = captureResults(sourcePane);

    if (!results.length && !htmlBlocks.length) {
        // Nothing has been calculated in this pane, so there is no scenario to save. The server rejects
        // this too (AC-2), but stopping here avoids a pointless round trip and a Fail record.
        return null;
    }

    const headline = buildHeadline(calculatorSlug, results, htmlBlocks);

    return {
        calculatorId: calculatorSlug,
        activeTab,
        headlineLabel: headline.label,
        headlineValue: headline.value,
        headlineSubLabel: headline.subLabel || null,
        inputs,
        results,
        amortizationSummary: activeTab === AMORTIZATION_PANE_ID ? captureAmortization(activePane) : null
    };
}

/** Formats one captured input for the summary line: "Loan $360,000", "30 yr", "6.5%". */
function summaryPart(input) {
    const label = (input.label || '').trim();
    let value = (input.value || '').trim();

    if (input.prefix && value.indexOf(input.prefix) !== 0) {
        value = input.prefix + value;
    }

    if (input.suffix && value.slice(-input.suffix.length) !== input.suffix) {
        value += input.suffix;
    }

    // A term input carries no unit icon; its unit lives in the label ("Loan Term (Yrs)").
    if (!input.prefix && !input.suffix && /\byrs?\b|\byears?\b/i.test(label)) {
        value += ' yr';
    }

    // A dollar figure reads best led by the label's first word, per the mockup: "Loan $360,000"
    // rather than a bare "$360,000".
    if (input.prefix === '$') {
        const word = label.split(/[\s(]+/)[0];
        if (word) {
            value = word + ' ' + value;
        }
    }

    return value;
}

/** Builds the one-line scenario summary shown in the modal, e.g. "Loan $360,000 · 30 yr · 6.5%". */
export function buildSummaryLine(capture) {
    if (!capture) return '';

    return capture.inputs.slice(0, 3).map(summaryPart).join(' · ');
}
